import Coordinates from '../model/coordinates';
import Address from '../model/project/address';
import { SubProject } from '../model/project/sub-project';
import ProjectHelper from './project-helper';

export default class ProjectMapHelper {
  public static readonly BELGIUM_GEOGRAPHICAL_CENTER = {
    lat: 50.641111,
    lng: 4.668056,
  };

  public static readonly INPRESS_COORDINATES = {
    lat: 51.1536601,
    lng: 4.377043,
  };

  public static readonly INPRESS_ADDRESS = new Address();

  public static readonly COORDINATE_UNIQUENESS_FACTOR = 1000;

  public static readonly HIDE_POI_STYLE = [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
  ];

  private constructor() {}

  static initializeInpressAddress(): void {
    // sadly there seems to be no visibility keyword that makes a function both private and accessible from within the same file
    this.INPRESS_ADDRESS.line1 = 'Terbekehofdreef 8, Unit A10';
    this.INPRESS_ADDRESS.postalCode = '2610';
    this.INPRESS_ADDRESS.city = 'Wilrijk';
    this.INPRESS_ADDRESS.coordinates = new Coordinates();
    this.INPRESS_ADDRESS.coordinates.lat = this.INPRESS_COORDINATES.lat;
    this.INPRESS_ADDRESS.coordinates.lng = this.INPRESS_COORDINATES.lng;
  }

  public static constructSubProjectGroups(
    subProjects: SubProject[]
  ): SubProjectGroup[] {
    if (subProjects == null) {
      return null;
    }
    if (subProjects.length === 0) {
      return [];
    }
    const toReturn: SubProjectGroup[] = [];
    const coordsMap = new Map<number, SubProjectGroup>();
    subProjects.forEach((sp) => {
      if (
        sp.project != null &&
        sp.project.baseProjectAddress != null &&
        sp.project.baseProjectAddress.coordinates != null
      ) {
        const key =
          sp.project.baseProjectAddress.coordinates.lat *
            this.COORDINATE_UNIQUENESS_FACTOR +
          sp.project.baseProjectAddress.coordinates.lng;
        let group = coordsMap.get(key);
        if (group == null) {
          group = {
            lat: sp.project.baseProjectAddress.coordinates.lat,
            lng: sp.project.baseProjectAddress.coordinates.lng,
            subProjects: [],
          };
          coordsMap.set(key, group);
        }
        group.subProjects.push(sp);
      }
    });
    coordsMap.forEach((group) => {
      group.markerColor = this.calculateGroupMarkerColor(group);
      toReturn.push(group);
    });
    return toReturn;
  }

  public static calculateGroupMarkerColor(group: SubProjectGroup): string {
    let toReturn: string = null;
    for (const subProject of group.subProjects) {
      const color =
        ProjectHelper.hasPlacer(subProject) &&
        subProject.currentConfirmedPlacementDate != null
          ? MARKER_COLORS.GRAY_MARKER
          : this.getToPlanMarkerColor(subProject);
      subProject.planned = color === MARKER_COLORS.GRAY_MARKER;
      if (
        toReturn == null ||
        (toReturn === MARKER_COLORS.GRAY_MARKER &&
          color !== MARKER_COLORS.GRAY_MARKER)
      ) {
        toReturn = color;
      }
    }
    return toReturn;
  }

  private static getToPlanMarkerColor(subProject: SubProject): string {
    switch (subProject.type) {
      case 'BaseProject':
        return MARKER_COLORS.GREEN_MARKER;
      case 'Adjustment':
        return MARKER_COLORS.BLUE_MARKER;
      case 'SoldRented':
        return MARKER_COLORS.YELLOW_MARKER;
      case 'Repo':
        return MARKER_COLORS.RED_MARKER;
    }
  }

  static fitBoundsToCoordinates(
    intermediateBounds: BoundsCoordinates,
    lat: number,
    lng: number
  ): BoundsCoordinates {
    if (lat == null || lng == null) {
      return intermediateBounds;
    }
    if (intermediateBounds == null) {
      return ProjectMapHelper.constructNewBounds(lat, lng);
    }
    ProjectMapHelper.updateBounds(intermediateBounds, lat, lng);
    return intermediateBounds;
  }

  static constructNewBounds(lat: number, lng: number): BoundsCoordinates {
    return {
      northBound: lat,
      southBound: lat,
      eastBound: lng,
      westBound: lng,
    };
  }

  private static updateBounds(
    intermediateBounds: BoundsCoordinates,
    lat: number,
    lng: number
  ): void {
    intermediateBounds.northBound =
      intermediateBounds.northBound == null
        ? lat
        : Math.max(intermediateBounds.northBound, lat);
    intermediateBounds.southBound =
      intermediateBounds.southBound == null
        ? lat
        : Math.min(intermediateBounds.southBound, lat);
    intermediateBounds.eastBound =
      intermediateBounds.eastBound == null
        ? lng
        : Math.max(intermediateBounds.eastBound, lng);
    intermediateBounds.westBound =
      intermediateBounds.westBound == null
        ? lng
        : Math.min(intermediateBounds.westBound, lng);
  }

  static toLatLngBounds(
    boundsCoordinates: BoundsCoordinates
  ): google.maps.LatLngBounds {
    return boundsCoordinates == null
      ? null
      : new google.maps.LatLngBounds(
          new google.maps.LatLng(
            boundsCoordinates.southBound,
            boundsCoordinates.westBound
          ),
          new google.maps.LatLng(
            boundsCoordinates.northBound,
            boundsCoordinates.eastBound
          )
        );
  }

  static toBoundsCoordinates(
    latLngBounds: google.maps.LatLngBounds
  ): BoundsCoordinates {
    return latLngBounds == null
      ? null
      : {
          northBound: latLngBounds.getNorthEast().lat(),
          eastBound: latLngBounds.getNorthEast().lng(),
          southBound: latLngBounds.getSouthWest().lat(),
          westBound: latLngBounds.getSouthWest().lng(),
        };
  }
}
ProjectMapHelper.initializeInpressAddress();

export interface SubProjectGroup {
  lat: number;
  lng: number;
  subProjects: SubProject[];
  markerColor?: string;
}

export enum MARKER_COLORS {
  GRAY_MARKER = 'Grijs.png',
  GREEN_MARKER = 'Groen.png',
  GREEN_MARKER_STONES = 'Groen-stenen.png',
  BLUE_MARKER = 'Blauw.png',
  BLUE_MARKER_STONES = 'Blauw-stenen.png',
  RED_MARKER = 'Rood.png',
  RED_MARKER_STONES = 'Rood-stenen.png',
  YELLOW_MARKER = 'Geel.png',
  YELLOW_MARKER_STONES = 'Geel-stenen.png',
  PURPLE_MARKER = 'Paars.png',
}

export interface BoundsCoordinates {
  northBound: number;
  southBound: number;
  eastBound: number;
  westBound: number;
}
